import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

const TONES = ["Warm & storytelling", "Punchy & bold", "Analytical & data-driven", "Playful & witty", "Reflective & philosophical"];

const ALL_PLATFORMS = [
    { id: "linkedin", label: "LinkedIn", hint: "Full post with hook, body, CTA, hashtags" },
    { id: "twitter", label: "X / Twitter", hint: "Thread or single tweet — punchy, under 280 chars" },
    { id: "reddit", label: "Reddit", hint: "Discussion-first, no hashtags, longer form ok" },
    { id: "instagram", label: "Instagram", hint: "Caption with line breaks and emojis" },
];

const TABS = [
    { id: "profile", label: "Voice Profile" },
    { id: "platforms", label: "Platforms" },
    { id: "brand-voice", label: "Brand Voice" },
];

function completenessScore(form, platforms, brandVoice) {
    const checks = [
        !!form.name,
        !!form.industry,
        !!form.experience,
        !!form.target_audience,
        !!form.bio,
        !!form.sample_post,
        platforms.length > 0,
        !!brandVoice.tone_words,
        !!brandVoice.sentence_style,
        !!brandVoice.vocabulary,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export default function Profile() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("profile");
    const [form, setForm] = useState({
        name: "", industry: "", experience: "", target_audience: "",
        tone: TONES[0], bio: "", sample_post: "",
    });
    const [platforms, setPlatforms] = useState(["linkedin"]);
    const [brandVoice, setBrandVoice] = useState({
        tone_words: "", sentence_style: "", vocabulary: "", avoid: "", pov: "",
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!user) return;
        setForm({
            name: user.name || "", industry: user.industry || "",
            experience: user.experience || "", target_audience: user.target_audience || "",
            tone: user.tone || TONES[0], bio: user.bio || "", sample_post: user.sample_post || "",
        });
        setPlatforms(user.preferred_platforms || ["linkedin"]);
        if (user.brand_voice) setBrandVoice(b => ({ ...b, ...user.brand_voice }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const togglePlatform = (id) => {
        setPlatforms(prev =>
            prev.includes(id) ? (prev.length > 1 ? prev.filter(p => p !== id) : prev) : [...prev, id]
        );
    };

    const save = async (e) => {
        e.preventDefault();
        setErr(""); setSaving(true);
        try {
            if (tab === "profile") {
                await api.post("/profile", form);
            } else if (tab === "platforms") {
                await api.post("/profile", { preferred_platforms: platforms });
            } else if (tab === "brand-voice") {
                await api.post("/profile/brand-voice", brandVoice);
            }
            await refreshUser();
            toast.success("Saved");
            if (tab === "profile") navigate("/workspace");
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full lk-border lk-surface-2 rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--lk-primary)] text-sm sm:text-base";
    const pct = completenessScore(form, platforms, brandVoice);

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                <div className="lk-label">Settings</div>
                <h1 className="font-heading text-3xl sm:text-5xl mt-2 tracking-tight">Your Lakshmi profile</h1>
                <p className="text-[var(--lk-muted)] mt-3 max-w-xl text-sm sm:text-base">Teach Lakshmi how you write, which platforms you use, and what your brand sounds like.</p>

                {/* Completeness meter */}
                <div className="mt-6 lk-border lk-surface rounded-xl p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[var(--lk-primary)]" />
                            <span className="text-sm font-medium">Voice profile completeness</span>
                        </div>
                        <span className="font-mono text-sm text-[var(--lk-primary)]">{pct}%</span>
                    </div>
                    <div className="h-2 lk-surface-2 rounded-full overflow-hidden lk-border">
                        <div
                            className="h-full bg-[var(--lk-primary)] rounded-full transition-all duration-500"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    {pct < 100 && (
                        <p className="text-xs text-[var(--lk-muted)] mt-2">
                            {pct < 40 ? "Just getting started — fill in your voice profile to improve output quality." :
                             pct < 70 ? "Good progress. Add sample post and brand voice for best results." :
                             "Almost there — complete the remaining fields for the sharpest output."}
                        </p>
                    )}
                    {pct === 100 && (
                        <p className="text-xs text-[var(--lk-primary)] mt-2">Profile complete. Lakshmi knows you well.</p>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mt-6 sm:mt-8 lk-border rounded-xl p-1 w-full sm:w-fit overflow-x-auto">
                    {TABS.map(t => (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className={`px-4 py-2 text-sm rounded-lg transition-colors whitespace-nowrap flex-1 sm:flex-none ${tab === t.id ? "bg-[var(--lk-primary)] text-black font-medium" : "text-[var(--lk-muted)] hover:text-[var(--lk-text)]"}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={save} className="mt-6 sm:mt-8">
                    {/* Profile tab */}
                    {tab === "profile" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                            <div>
                                <label className="lk-label block mb-2">Name</label>
                                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Full name" data-testid="profile-name-input" />
                            </div>
                            <div>
                                <label className="lk-label block mb-2">Industry</label>
                                <input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} className={inputCls} placeholder="e.g. AI infra, Fintech" data-testid="profile-industry-input" />
                            </div>
                            <div>
                                <label className="lk-label block mb-2">Experience</label>
                                <input value={form.experience} onChange={e => setForm({ ...form, experience: e.target.value })} className={inputCls} placeholder="e.g. 8 yrs building ML platforms" data-testid="profile-experience-input" />
                            </div>
                            <div>
                                <label className="lk-label block mb-2">Target Audience</label>
                                <input value={form.target_audience} onChange={e => setForm({ ...form, target_audience: e.target.value })} className={inputCls} placeholder="e.g. Founders, engineering leads" data-testid="profile-audience-input" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="lk-label block mb-2">Writing Tone</label>
                                <select value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })} className={inputCls} data-testid="profile-tone-select">
                                    {TONES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="lk-label block mb-2">Short bio</label>
                                <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} className={inputCls} rows={3} placeholder="One paragraph on who you are." data-testid="profile-bio-input" />
                            </div>

                            {/* Sample post — highlighted as the most important field */}
                            <div className="sm:col-span-2 rounded-xl border border-[var(--lk-primary)]/35 bg-[var(--lk-primary)]/5 p-4 sm:p-5">
                                <div className="flex items-center gap-2 mb-1">
                                    <Sparkles className="w-4 h-4 text-[var(--lk-primary)]" />
                                    <label className="text-sm font-medium text-[var(--lk-primary)]">
                                        Your voice fingerprint
                                    </label>
                                </div>
                                <p className="text-xs text-[var(--lk-muted)] mb-3">
                                    Paste a LinkedIn post you're proud of. Every future draft is calibrated against this. The more authentic, the better the output.
                                </p>
                                <textarea
                                    value={form.sample_post}
                                    onChange={e => setForm({ ...form, sample_post: e.target.value })}
                                    className={`${inputCls} border-[var(--lk-primary)]/30 focus:border-[var(--lk-primary)]`}
                                    rows={6}
                                    placeholder="Paste your best post here..."
                                    data-testid="profile-sample-input"
                                />
                            </div>
                        </div>
                    )}

                    {/* Platforms tab */}
                    {tab === "platforms" && (
                        <div>
                            <p className="text-[var(--lk-muted)] text-sm mb-5">Select which platforms Lakshmi generates content for. You can change this anytime.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                {ALL_PLATFORMS.map(p => (
                                    <button key={p.id} type="button" onClick={() => togglePlatform(p.id)}
                                        className={`lk-border rounded-xl p-4 sm:p-5 text-left transition-all ${platforms.includes(p.id) ? "border-[var(--lk-primary)] bg-[var(--lk-primary)]/10" : "lk-surface-2"}`}
                                        data-testid={`platform-toggle-${p.id}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="font-heading text-lg sm:text-xl">{p.label}</div>
                                            <div className={`w-2 h-2 rounded-full ${platforms.includes(p.id) ? "bg-[var(--lk-primary)]" : "bg-[var(--lk-border)]"}`} />
                                        </div>
                                        <div className={`text-xs mt-1.5 leading-relaxed ${platforms.includes(p.id) ? "text-[var(--lk-primary)]" : "text-[var(--lk-muted)]"}`}>
                                            {p.hint}
                                        </div>
                                        <div className={`text-xs mt-2 font-medium ${platforms.includes(p.id) ? "text-[var(--lk-primary)]" : "text-[var(--lk-muted)]"}`}>
                                            {platforms.includes(p.id) ? "Active" : "Inactive"}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Brand Voice tab */}
                    {tab === "brand-voice" && (
                        <div className="space-y-4 sm:space-y-5">
                            <p className="text-[var(--lk-muted)] text-sm">Define your brand voice. Lakshmi enforces this on every post.</p>
                            <div>
                                <label className="lk-label block mb-2">Tone words <span className="normal-case tracking-normal text-[var(--lk-muted)] font-normal">(3–5 adjectives)</span></label>
                                <input value={brandVoice.tone_words} onChange={e => setBrandVoice({ ...brandVoice, tone_words: e.target.value })} className={inputCls} placeholder="e.g. Direct, analytical, no-fluff" data-testid="bv-tone-input" />
                            </div>
                            <div>
                                <label className="lk-label block mb-2">Sentence style</label>
                                <input value={brandVoice.sentence_style} onChange={e => setBrandVoice({ ...brandVoice, sentence_style: e.target.value })} className={inputCls} placeholder="e.g. Short punchy sentences. No nested clauses." data-testid="bv-style-input" />
                            </div>
                            <div>
                                <label className="lk-label block mb-2">Vocabulary to use</label>
                                <input value={brandVoice.vocabulary} onChange={e => setBrandVoice({ ...brandVoice, vocabulary: e.target.value })} className={inputCls} placeholder="e.g. ship, system, leverage, first principles" data-testid="bv-vocab-input" />
                            </div>
                            <div>
                                <label className="lk-label block mb-2">Words / phrases to avoid</label>
                                <input value={brandVoice.avoid} onChange={e => setBrandVoice({ ...brandVoice, avoid: e.target.value })} className={inputCls} placeholder="e.g. excited, thrilled, synergy, game-changer" data-testid="bv-avoid-input" />
                            </div>
                            <div>
                                <label className="lk-label block mb-2">Point of view</label>
                                <input value={brandVoice.pov} onChange={e => setBrandVoice({ ...brandVoice, pov: e.target.value })} className={inputCls} placeholder="e.g. First person, speak directly to reader, share personal stories" data-testid="bv-pov-input" />
                            </div>

                            {/* Preview hint */}
                            {(brandVoice.tone_words || brandVoice.sentence_style) && (
                                <div className="lk-border lk-surface-2 rounded-xl p-4 text-sm">
                                    <div className="lk-label mb-2">How this sounds</div>
                                    <p className="italic text-[var(--lk-muted)] leading-relaxed">
                                        {brandVoice.sentence_style?.toLowerCase().includes("short")
                                            ? `"Made a mistake last week. Learned something huge. Here's what happened."`
                                            : `"I've been thinking about ${brandVoice.tone_words?.split(",")[0]?.trim().toLowerCase() || "this"} lately — and what I've found might surprise you."`
                                        }
                                    </p>
                                    <p className="text-xs text-[var(--lk-muted)] mt-2 opacity-70">Approximate — actual output will match your voice fingerprint above.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {err && <div className="mt-5 text-sm text-red-400" data-testid="profile-error">{err}</div>}

                    <div className="mt-6 sm:mt-8 flex justify-end">
                        <button type="submit" disabled={saving}
                            className="lk-btn-primary rounded-full px-8 py-3 disabled:opacity-50 w-full sm:w-auto"
                            data-testid="profile-save-btn">
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
